/**
 * Pure turn/block accumulation for the agent stream.
 *
 * A turn holds an ORDERED `blocks` array rather than "a text string plus a
 * chip list" — that is what preserves the text -> tool -> text interleaving a
 * turn actually produces. `callIndex` maps callId -> block index so a
 * tool_result resolves its chip in O(1), which matters because concurrent
 * tool calls can resolve out of order.
 *
 * Dependency-free on purpose so it stays unit-testable.
 */

export const AGENT_TURN_STATUS = {
    RUNNING: 'running',
    AWAITING_CONFIRMATION: 'awaiting_confirmation',
    AWAITING_AUTH: 'awaiting_auth',
    COMPLETE: 'complete',
    FAILED: 'failed',
    LIMIT_REACHED: 'limit_reached',
    STOPPED: 'stopped',
};

export const TERMINAL_EVENT_TYPES = ['tool_confirm', 'auth_expired', 'limit_reached', 'error', 'done'];

export function isTerminalEvent(event) {
    return TERMINAL_EVENT_TYPES.includes(event?.type);
}

function emptyTurn(turnId, role, status) {
    return {
        turnId,
        role,
        status,
        blocks: [],
        callIndex: {},
        attachments: [],
        stopReason: null,
        usage: null,
        error: null,
        limit: null,
        pending: null,
        // Which tiers this turn ran under. `turnMode` is what was requested
        // (rehydrated turns carry the API's authoritative value; a locally
        // created turn is seeded with what the user asked for, see
        // createAssistantTurn); `rolesUsed` is filled in only by the API — a
        // live turn's escalation shows up as `model_switch` blocks instead.
        turnMode: null,
        rolesUsed: [],
        preset: null,
    };
}

export function createUserTurn(turnId, text, attachments) {
    const turn = emptyTurn(turnId, 'user', AGENT_TURN_STATUS.COMPLETE);

    return Object.assign({}, turn, {
        blocks: [{type: 'text', text}],
        // Files the user attached to this message. The agent reads them
        // server-side; the frontend only ever shows filename and size.
        attachments: attachments ?? [],
    });
}

/**
 * `requestedMode`/`preset` are what the user asked for when they sent the
 * message — an optimistic seed for the mode/preset badge while the turn is
 * still running, before any `model_switch` event or a reload's authoritative
 * `rolesUsed` can confirm it. The server may resolve 'auto' differently, but
 * showing nothing until completion would make the badge feel broken on every
 * single turn, since most turns never escalate.
 */
/**
 * Concatenates a turn's text blocks — what the user typed, or the prose an
 * assistant turn produced, with tool chips and other block types excluded.
 * Used by the user bubble and by "retry with deeper reasoning", which resends
 * exactly this text under a different mode.
 */
export function turnText(turn) {
    return (turn?.blocks ?? [])
        .map((block) => (block.type === 'text' ? block.text : ''))
        .join('');
}

export function createAssistantTurn(turnId, requestedMode, preset) {
    const turn = emptyTurn(turnId, 'assistant', AGENT_TURN_STATUS.RUNNING);

    return Object.assign({}, turn, {
        turnMode: requestedMode ?? null,
        preset: preset ?? null,
    });
}

function appendText(turn, text) {
    const blocks = turn.blocks.slice();
    const last = blocks[blocks.length - 1];

    if (last && last.type === 'text') {
        blocks[blocks.length - 1] = {type: 'text', text: last.text + text};
    } else {
        blocks.push({type: 'text', text});
    }

    return Object.assign({}, turn, {blocks});
}

function addToolChip(turn, event) {
    const blocks = turn.blocks.slice();

    blocks.push({
        type: 'tool',
        callId: event.callId,
        name: event.name,
        source: event.source ?? null,
        argsSummary: event.argsSummary ?? null,
        state: 'running',
        ok: null,
        rowCount: null,
        durationMs: null,
    });

    return Object.assign({}, turn, {
        blocks,
        callIndex: Object.assign({}, turn.callIndex, {[event.callId]: blocks.length - 1}),
    });
}

function resolveToolChip(turn, event) {
    const index = turn.callIndex[event.callId];

    if (index === undefined || !turn.blocks[index]) {
        return turn;                                  // result for a chip we never saw
    }

    const blocks = turn.blocks.slice();
    blocks[index] = Object.assign({}, blocks[index], {
        // ok:false is the model self-correcting, NOT a turn failure.
        state: event.ok ? 'done' : 'error',
        ok: event.ok ?? null,
        rowCount: event.rowCount ?? null,
        durationMs: event.durationMs ?? null,
    });

    return Object.assign({}, turn, {blocks});
}

/**
 * `model_switched` is NOT terminal (it is absent from TERMINAL_EVENT_TYPES
 * below) — the run escalated mid-turn and keeps streaming on the new model.
 * Recorded as a block, like a tool chip, so it appears in the timeline at the
 * point it actually happened rather than as a property that would only ever
 * describe the turn's end state.
 */
function addModelSwitch(turn, event) {
    return Object.assign({}, turn, {
        blocks: turn.blocks.concat([{
            type: 'model_switch',
            from: event.from ?? null,
            to: event.to ?? null,
            role: event.role ?? null,
            reason: event.reason ?? null,
        }]),
    });
}

function addFileCard(turn, event) {
    return Object.assign({}, turn, {
        blocks: turn.blocks.concat([{
            type: 'file',
            url: event.url,
            filename: event.filename,
            sizeBytes: event.sizeBytes ?? null,
            readyAt: event.readyAt ?? null,
        }]),
    });
}

export function applyEvent(turn, event) {
    switch (event?.type) {
        case 'text_delta':
            return appendText(turn, event.text ?? '');

        case 'tool_use':
            return addToolChip(turn, event);

        case 'tool_result':
            return resolveToolChip(turn, event);

        case 'file_ready':
            return addFileCard(turn, event);

        case 'model_switched':
            return addModelSwitch(turn, event);

        case 'tool_confirm':
            return Object.assign({}, turn, {
                status: AGENT_TURN_STATUS.AWAITING_CONFIRMATION,
                pending: {
                    callId: event.callId,
                    toolName: event.name,
                    summary: event.summary ?? null,
                    args: event.args ?? {},
                    // The resume cursor travels ON the card, not in stream state:
                    // the decision may arrive minutes later, after the stream has
                    // gone idle (which resets stream.lastSeq to 0). Reattaching at
                    // 0 would replay this turn on top of the blocks we already
                    // accumulated and print the whole answer twice.
                    lastSeq: typeof event.seq === 'number' ? event.seq : 0,
                },
            });

        case 'auth_expired':
            return Object.assign({}, turn, {status: AGENT_TURN_STATUS.AWAITING_AUTH});

        case 'limit_reached':
            return Object.assign({}, turn, {
                status: AGENT_TURN_STATUS.LIMIT_REACHED,
                limit: {limit: event.limit ?? null, completed: event.completed ?? null},
            });

        case 'error':
            return Object.assign({}, turn, {
                status: AGENT_TURN_STATUS.FAILED,
                error: {code: event.code ?? null, message: event.message ?? null},
            });

        case 'done':
            return Object.assign({}, turn, {
                status: event.stopReason === 'stopped'
                    ? AGENT_TURN_STATUS.STOPPED
                    : AGENT_TURN_STATUS.COMPLETE,
                stopReason: event.stopReason ?? null,
                usage: event.usage ?? null,
            });

        default:
            return turn;
    }
}

/**
 * A rehydrated `limit_reached` turn MUST stay marked as cut short. Rendering it
 * as a plain assistant bubble is the worst failure mode in the design — a
 * dispatcher acting on a half-answer — so when the API gives no detail we still
 * return a non-null marker so the banner renders.
 */
function limitFromApi(apiTurn) {
    if (apiTurn.status !== AGENT_TURN_STATUS.LIMIT_REACHED) {
        return null;
    }

    if (apiTurn.limit && typeof apiTurn.limit === 'object') {
        return apiTurn.limit;
    }

    return {limit: apiTurn.limit ?? null, completed: apiTurn.completed ?? null};
}

/**
 * Rehydrates a turn from GET /v1/threads/:id. The API shape carries `text` and
 * `toolSummaries` with no interleaving information, so it renders as one text
 * block followed by a collapsed summaries group.
 */
export function turnFromApi(apiTurn) {
    const turn = emptyTurn(apiTurn.turnId, apiTurn.role, apiTurn.status);
    const blocks = [];
    const callIndex = {};

    if (apiTurn.text) {
        blocks.push({type: 'text', text: apiTurn.text});
    }

    (apiTurn.toolSummaries ?? []).forEach((summary, i) => {
        const callId = summary.ref ?? `summary_${i}`;
        callIndex[callId] = blocks.length;
        blocks.push({
            type: 'tool',
            callId,
            name: summary.name,
            source: null,
            argsSummary: null,
            state: 'done',
            ok: true,
            rowCount: summary.rowCount ?? null,
            durationMs: null,
        });
    });

    return Object.assign({}, turn, {
        blocks,
        callIndex,
        // Present on user turns; this is what makes an attachment chip survive
        // a reload rather than living only in the optimistic local turn.
        attachments: apiTurn.attachments ?? [],
        stopReason: apiTurn.stopReason ?? null,
        limit: limitFromApi(apiTurn),
        // Authoritative once the API has a turn to report on — this is what
        // makes the "already ran on thinking" check (turnUsedRole) correct
        // after a reload, when no model_switch block survived the flatten.
        rolesUsed: apiTurn.rolesUsed ?? [],
        turnMode: apiTurn.turnMode ?? null,
        preset: apiTurn.preset ?? null,
    });
}
